import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { alfredDeviceService } from "@/services/alfred-device-service"
import type {
  AlfredDeviceListParams,
  BulkUpdateAlfredDevicesRequest,
  CloneAlfredDeviceRequest,
  CreateAlfredDeviceRequest,
  UpdateAlfredDeviceRequest,
} from "@/types/alfred-device"
import { isNotFoundError } from "@/types/api"

const alfredDeviceSwrConfig = {
  revalidateOnFocus: false,
  shouldRetryOnError: (error: unknown) => !isNotFoundError(error),
}

// The list hooks accept the full param set so filter/sort/pagination all flow to the
// API. Every param is part of the SWR key, so changing any of them refetches and the
// active filter/sort persists as the user pages through results.
export function useAlfredDevices(params: AlfredDeviceListParams = {}, enabled = true) {
  const { page = 1, pageSize = 50, filter, sortBy, sortOrder } = params
  return useSWR(
    enabled ? ["alfred", "devices", page, pageSize, filter ?? "", sortBy ?? "", sortOrder ?? ""] : null,
    () => alfredDeviceService.getAll({ page, pageSize, filter, sortBy, sortOrder }),
    alfredDeviceSwrConfig,
  )
}

export function useAlfredDevicesByClient(
  clientId: number,
  params: AlfredDeviceListParams = {},
  enabled = true,
) {
  const { page = 1, pageSize = 50, filter, sortBy, sortOrder } = params
  return useSWR(
    clientId && enabled
      ? ["alfred", "clients", clientId, "devices", page, pageSize, filter ?? "", sortBy ?? "", sortOrder ?? ""]
      : null,
    () => alfredDeviceService.getByClient(clientId, { page, pageSize, filter, sortBy, sortOrder }),
    alfredDeviceSwrConfig,
  )
}

export function useAlfredDevice(id: number) {
  return useSWR(
    id ? ["alfred", "devices", id] : null,
    () => alfredDeviceService.getById(id),
    alfredDeviceSwrConfig,
  )
}

export function useUpdateAlfredDeviceById(id: number) {
  return useSWRMutation(
    ["alfred", "devices", id],
    (_key: unknown[], { arg }: { arg: UpdateAlfredDeviceRequest }) =>
      alfredDeviceService.update(id, arg),
  )
}

export function useCreateAlfredDevice() {
  return useSWRMutation(
    ["alfred", "devices"],
    (_key: string[], { arg }: { arg: CreateAlfredDeviceRequest }) =>
      alfredDeviceService.create(arg),
  )
}

export function useCloneAlfredDevice() {
  return useSWRMutation(
    ["alfred", "devices", "clone"],
    (
      _key: string[],
      { arg }: { arg: { sourceDevicePk: number; data: CloneAlfredDeviceRequest } },
    ) => alfredDeviceService.clone(arg.sourceDevicePk, arg.data),
  )
}

export function useUpdateAlfredDevice() {
  return useSWRMutation(
    ["alfred", "devices"],
    (_key: string[], { arg }: { arg: { id: number; data: UpdateAlfredDeviceRequest } }) =>
      alfredDeviceService.update(arg.id, arg.data),
  )
}

export function useBulkUpdateAlfredDevices() {
  return useSWRMutation(
    ["alfred", "devices", "bulk"],
    (_key: string[], { arg }: { arg: BulkUpdateAlfredDevicesRequest }) =>
      alfredDeviceService.bulkUpdate(arg),
  )
}

export function useBulkDeleteAlfredDevices() {
  return useSWRMutation(
    ["alfred", "devices", "bulk-delete"],
    (_key: string[], { arg }: { arg: number[] }) =>
      alfredDeviceService.bulkDelete(arg),
  )
}

export function useDeleteAlfredDevice() {
  return useSWRMutation(
    ["alfred", "devices"],
    (_key: string[], { arg }: { arg: number }) =>
      alfredDeviceService.delete(arg),
  )
}
